import React, { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { MdArrowRight } from 'react-icons/md';

import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Link from 'next/link';
import { useContextSelector } from 'use-context-selector';

import { amplitudeInstance } from '../../config/amplitude';
import env from '../../config/env';
import AppContext from '../../context/AppContext';
import buildRouteUrl from '../../helpers/buildRouteUrl';
import { WatchProvider } from '../../types/types';
import { NextButton, PrevButton } from '../EmblaCarouselButtons';

interface PropTypes {
  id: string;
  title: string;
  loadMore?: boolean;
  totalItems: number;
  type: 'resource' | 'watch_provider' | 'person' | 'genre' | 'list';
  children: React.ReactNode;
  rightButtonIcon?: React.ReactNode;
  onLoadMoreClick?: (
    event?: React.MouseEvent<HTMLHeadingElement | HTMLButtonElement>
  ) => void;
  onRightButtonClick?: (
    event?: React.MouseEvent<HTMLHeadingElement | HTMLButtonElement>
  ) => void;
  watchProvider?: WatchProvider;
  className?: string;
}

function CarouselSection({
  id,
  title,
  loadMore = false,
  totalItems,
  type,
  children,
  onLoadMoreClick,
  rightButtonIcon,
  onRightButtonClick,
  watchProvider,
  className
}: PropTypes) {
  const getCarouselIndex = useContextSelector(
    AppContext,
    app => app.getCarouselIndex
  );
  const saveCarouselScrollIndex = useContextSelector(
    AppContext,
    app => app.saveCarouselScrollIndex
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    axis: 'x',
    align: 'start',
    speed: 30,
    slidesToScroll: 'auto',
    containScroll: 'trimSnaps',
    dragFree: isMobile,
    draggable: true
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    saveCarouselScrollIndex(id, emblaApi.selectedScrollSnap());

    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    emblaApi.on('resize', () => {
      emblaApi.reInit();
      onSelect();
    });
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    const savedCarouselIndex = getCarouselIndex(id);
    if (savedCarouselIndex) {
      emblaApi.scrollTo(savedCarouselIndex, true);
    }
  }, [emblaApi]);

  let buttonsHeight = 100;
  let smallButtonsHeight = 60;

  if (type === 'resource') {
    buttonsHeight = 210;
    smallButtonsHeight = 150;
  }

  if (type === 'watch_provider') {
    buttonsHeight = 90;
    smallButtonsHeight = 70;
  }

  if (type === 'person') {
    buttonsHeight = 210;
    smallButtonsHeight = 150;
  }

  if (type === 'genre') {
    buttonsHeight = 130;
    smallButtonsHeight = 100;
  }

  if (type === 'list') {
    buttonsHeight = 151.69;
    smallButtonsHeight = 112.36;
  }

  if (totalItems > 0) {
    return (
      <div className={`flex flex-col items-start justify-start ${className}`}>
        <div className="flex flex-row w-full justify-between items-center mb-2">
          <div className="flex flex-row items-center">
            <div className="flex flex-row gap-x-2">
              {!watchProvider && (
                <h2
                  className={`text-white text-2xl font-normal  ${
                    loadMore ? 'cursor-pointer' : 'cursor-default'
                  }`}
                  onClick={onLoadMoreClick}
                >
                  {title}
                </h2>
              )}
              {!!watchProvider && (
                <Link
                  href={buildRouteUrl(
                    `/watch-providers/${watchProvider.slug}/all`
                  )}
                  className={`text-white text-2xl font-normal cursor-pointer hover:opacity-[0.5]`}
                >
                  {title}
                </Link>
              )}
              {!!watchProvider && (
                <Link
                  href={buildRouteUrl(
                    `/watch-providers/${watchProvider.slug}/all`
                  )}
                  onClick={() => {
                    amplitudeInstance.logEvent(
                      'Go To Watch Provider Contents Page From Catalog Streaming Trending Section',
                      {
                        watch_provider_name: watchProvider.name,
                        watch_provider_slug: watchProvider.slug,
                        watch_provider_id: watchProvider._id
                      }
                    );
                  }}
                >
                  <Image
                    className="transition-all-[250ms] hover:opacity-[0.4] cursor-pointer rounded-md w-[30px] h-[30px]"
                    sizes="
                    (min-width: 300px) 30px,30px
                  "
                    unoptimized
                    width={30}
                    height={30}
                    src={`${env.CDN_URL}${watchProvider?.icon}`}
                    alt={watchProvider.name}
                  />
                </Link>
              )}
            </div>
            {loadMore && (
              <button onClick={onLoadMoreClick}>
                <MdArrowRight size={20} className="text-white" />
              </button>
            )}
          </div>

          {rightButtonIcon && (
            <button onClick={onRightButtonClick}>{rightButtonIcon}</button>
          )}
        </div>
        <div className="embla">
          <div className="embla__viewport" ref={emblaRef}>
            <div className="embla__container">{children}</div>
            {!isMobile &&
              (emblaApi?.slidesInView().length || 0) < totalItems && (
                <>
                  {prevBtnEnabled && (
                    <div
                      className={`embla__button__container embla__button__container__left h-[${smallButtonsHeight}px] md:h-[${buttonsHeight}px] max-h-[${smallButtonsHeight}px] md:max-h-[${buttonsHeight}px]`}
                    >
                      <PrevButton
                        onClick={scrollPrev}
                        enabled={prevBtnEnabled}
                      />
                    </div>
                  )}
                  {nextBtnEnabled && (
                    <div
                      className={`embla__button__container embla__button__container__right h-[${smallButtonsHeight}px] md:h-[${buttonsHeight}px] max-h-[${smallButtonsHeight}px] md:max-h-[${buttonsHeight}px]`}
                    >
                      <NextButton
                        onClick={scrollNext}
                        enabled={nextBtnEnabled}
                      />
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      </div>
    );
  }

  return <></>;
}

export default React.memo(CarouselSection);
